import type { EiInvestigationRun } from '../types/engineeringIntelligence';

/** Operator guidance persisted on bootstrap when human_escalation is cleared. */
export function operatorGuidanceFromBootstrap(bootstrap?: Record<string, unknown>) {
  const g = bootstrap?.operator_guidance;
  if (!g || typeof g !== 'object') return null;
  const og = g as Record<string, unknown>;
  const message =
    typeof og.message === 'string'
      ? og.message
      : typeof og.choice === 'string'
        ? og.choice
        : '';
  if (!message && typeof og.result_note !== 'string') return null;
  return {
    operator: typeof og.operator === 'string' ? og.operator : undefined,
    message,
    result_note: typeof og.result_note === 'string' ? og.result_note : undefined,
    decision: typeof og.decision === 'string' ? og.decision : undefined,
    applied: og.applied === true,
    at: typeof og.at === 'string' ? og.at : undefined,
  };
}

export function isOperatorGuidanceApplied(bootstrap?: Record<string, unknown>): boolean {
  const g = bootstrap?.operator_guidance;
  if (!g || typeof g !== 'object') return false;
  return (g as Record<string, unknown>).applied === true;
}

export function hasOperatorGuidanceRecorded(run?: EiInvestigationRun | null): boolean {
  if (!run) return false;
  return Boolean(run.human_escalation?.operator_response ?? operatorGuidanceFromBootstrap(run.bootstrap));
}

/** The escalation duplicates an answered question that still covers it. A
 * rerun answer stops covering it once the rerun produced a newer mission
 * report: the stop after that rerun is a new decision. Mirrors the backend's
 * rerunAnswerConsumed. */
export function duplicateAnswerStillCovers(run?: EiInvestigationRun | null): boolean {
  const id = run?.human_escalation?.duplicate_answered_question_id;
  if (!id) return false;
  const aq = run?.answered_questions?.find((q) => q.id === id);
  if (!aq || (aq.decision !== 'rerun_tests')) return true;
  const reportAt = Date.parse(String(run?.mission_test_report?.executed_at || ''));
  const answeredAt = Date.parse(String(aq.answered_at || ''));
  return !(reportAt > answeredAt);
}

/** NEEDS_HUMAN with an open escalation that still needs a choice. */
export function needsOpenOperatorInput(run?: EiInvestigationRun | null): boolean {
  if (!run || String(run.state).toUpperCase() !== 'NEEDS_HUMAN') return false;
  const human = run.human_escalation;
  if (human?.operator_response) return false;
  if (duplicateAnswerStillCovers(run)) return false;
  // Applied guidance means the operator already answered — stale re-escalation
  // must not re-open the interactive panel (backend may attach a fresh
  // human_escalation without operator_response while automation resumes).
  if (isOperatorGuidanceApplied(run.bootstrap)) return false;
  if (!human && hasOperatorGuidanceRecorded(run)) return false;
  return Boolean(human);
}

/** Guidance recorded but automation has not left NEEDS_HUMAN yet. */
export function operatorGuidanceAwaitingResume(run?: EiInvestigationRun | null): boolean {
  if (!run || String(run.state).toUpperCase() !== 'NEEDS_HUMAN') return false;
  return hasOperatorGuidanceRecorded(run) && !needsOpenOperatorInput(run);
}

/** NEEDS_HUMAN because the engineering gate refused it: it waits on the root
 * cause analysis or the ownership decision, not on an answer in this run.
 * The backend derives engineering_gate_parked from the gate's own open
 * question; the bootstrap marker alone can outlive it, so it is only a
 * fallback for responses that predate the field. */
export function engineeringGateParked(run?: EiInvestigationRun | null): boolean {
  if (!run || String(run.state).toUpperCase() !== 'NEEDS_HUMAN') return false;
  if (typeof run.engineering_gate_parked === 'boolean') return run.engineering_gate_parked;
  return run.bootstrap?.engineering_gate_parked === true;
}

/** What the engineering gate is waiting on, in the gate's own words. */
export function engineeringGateMessage(run?: EiInvestigationRun | null): string {
  const unmet = (run?.human_escalation?.unknowns ?? []).filter(Boolean);
  const base = 'Engineering is waiting on the root cause analysis — add evidence and re-run RCA, or change the owner';
  return unmet.length ? `${base}. Unmet: ${unmet.join('; ')}` : base;
}
